import { Component, ChangeDetectorRef } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { SessionService } from "src/app/session.service";
import { DashboardService } from "../../dashboard.service";
import { GenericWidgetComponent } from "../generic-widget/generic-widget.component";
import { Widget } from "../widgets";
import { PermissionService } from "src/app/permission.service";
import {
  FEATURE_AURA_KEY,
  FEATURE_EXPLORIST_KEY,
  FEATURE_TRIPIST_KEY,
} from "src/app/features.constants";
import { IntegrationsService } from "src/app/integrations/integrations.service";
import { take } from "rxjs/operators";
import { forkJoin } from "rxjs";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

@Component({
  selector: "app-assistant-threads-widget",
  templateUrl: "./assistant-threads-widget.component.html",
  styleUrls: ["./assistant-threads-widget.component.scss"],
})
export class AssistantThreadsWidgetComponent extends GenericWidgetComponent {
  widget!: Widget;
  threadsChartOptions: any;
  totalThreads: any;
  totalAnonymous: any;
  totalAuthenticated: any;
  threadsData: any;
  enabledIntegrationTypes: Array<'tripist' | 'explorist' | 'aura'> = [];
  primaryIntegrationType: 'tripist' | 'explorist' | 'aura' | null = null;
  integrationIds: number[] = [];
  noIntegrationFound: boolean = false;
  bucket: "day" | "week" | "month" | "quarter" | "year" = "day";

  constructor(
    private DashboardService: DashboardService,
    private SessionService: SessionService,
    private PermissionService: PermissionService,
    private IntegrationsService: IntegrationsService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  get accountID() {
    return this.SessionService.currentAccountValue!.accountID;
  }

  get accountAppID() {
    return this.SessionService.currentAppValue!.id;
  }

  async ngOnInit(): Promise<void> {
    await this.determineIntegrationType();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    // Don't call initData here - wait for integration detection to complete
    // this.initData();

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        if (
          this.filterForm.valid &&
          this.filterForm.controls.startDate.value <
            this.filterForm.controls.endDate.value
        ) {
          this.initData();
        }
      });
  }

  private async determineIntegrationType(): Promise<void> {
    const enabledTypes: Array<'tripist' | 'explorist' | 'aura'> = [];
    if (this.PermissionService.hasFeature(FEATURE_TRIPIST_KEY, this.SessionService.currentAccountValue)) {
      enabledTypes.push('tripist');
    }
    if (this.PermissionService.hasFeature(FEATURE_EXPLORIST_KEY, this.SessionService.currentAccountValue)) {
      enabledTypes.push('explorist');
    }
    if (this.PermissionService.hasFeature(FEATURE_AURA_KEY, this.SessionService.currentAccountValue)) {
      enabledTypes.push('aura');
    }

    this.enabledIntegrationTypes = enabledTypes;

    const integrations = await this.getIntegrations();
    const providerByType: Record<'tripist' | 'explorist' | 'aura', string> = {
      tripist: 'tripist_assistant',
      explorist: 'explorist_assistant',
      aura: 'aura_assistant',
    };
    const preferredType = this.widget?.widgetDTO?.assistantType;
    const orderedTypes = (
      preferredType && enabledTypes.includes(preferredType)
        ? [preferredType, ...enabledTypes.filter((type) => type !== preferredType)]
        : enabledTypes
    ) as Array<'tripist' | 'explorist' | 'aura'>;

    let selectedType: 'tripist' | 'explorist' | 'aura' | null = null;
    let selectedId: number | null = null;
    for (const type of orderedTypes) {
      const integration = integrations.find((item: any) => item.provider?.type === providerByType[type]);
      if (integration?.id) {
        selectedType = type;
        selectedId = integration.id;
        break;
      }
    }

    this.primaryIntegrationType = selectedType ?? ((preferredType as any) || null);
    this.integrationIds = selectedId ? [selectedId] : [];
    this.noIntegrationFound = !selectedId;
    
    // Force change detection
    this.cdr.detectChanges();
    
    // Now that integration detection is complete, initialize data
    this.initData();
  }

  private async getIntegrations(): Promise<any[]> {
    try {
      const accountId = this.SessionService.currentAccountValue?.accountID;
      const appId = this.SessionService.currentAppValue?.id;
      
      if (!accountId || !appId) {
        console.warn('Missing session data for integration lookup');
        return [];
      }
      
      const response: any = await this.IntegrationsService.getIntegrations(
        accountId,
        appId,
        true
      ).pipe(take(1)).toPromise();
      
      // Handle both array response and object with records property
      return Array.isArray(response) ? response : response?.records || [];
    } catch (error) {
      console.error(`Error fetching integrations for assistant widgets:`, error);
      return [];
    }
  }

  get widgetTitle(): string {
    return `${this.primaryAssistantLabel} Threads`;
  }

  get enabledAssistantLabel(): string {
    const labels: Record<'tripist' | 'explorist' | 'aura', string> = {
      tripist: 'Tripist',
      explorist: 'Explorist',
      aura: 'Aura',
    };
    return this.enabledIntegrationTypes.map((type) => labels[type]).join(', ') || 'Assistant';
  }

  get primaryAssistantLabel(): string {
    const labels: Record<'tripist' | 'explorist' | 'aura', string> = {
      tripist: 'Tripist',
      explorist: 'Explorist',
      aura: 'Aura',
    };
    return this.primaryIntegrationType ? labels[this.primaryIntegrationType] : 'Assistant';
  }

  initData() {
    if (this.noIntegrationFound) {
      console.warn('No integration found for Assistant Threads widget');
      return;
    }

    if (this.integrationIds.length === 0) {
      console.warn('No integration IDs available for Assistant Threads widget');
      return;
    }

    this.filterForm.controls.endDate.value.setHours(23, 59, 59);

    const requests = this.integrationIds.map((integrationId) =>
      this.DashboardService.getAssistantThreads(
        this.accountID,
        this.accountAppID,
        integrationId,
        this.filterForm.controls.startDate.value,
        this.filterForm.controls.endDate.value,
      )
    );

    this.elementLoader
      .waitForEvent(forkJoin(requests))
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((responses) => {
        this.threadsData = this.mergeThreadResponses(responses as any[]);
        this.bucket = this.threadsData.bucket || "day";
        
        if (this.threadsData.data && this.threadsData.data.length > 0) {
          // Calculate the three metrics by summing the daily values
          this.totalThreads = this.threadsData.data.reduce((sum: number, day: any) => 
            sum + parseInt(day.daily_total_threads), 0);
          this.totalAnonymous = this.threadsData.data.reduce((sum: number, day: any) => 
            sum + parseInt(day.daily_anonymous_threads), 0);
          this.totalAuthenticated = this.threadsData.data.reduce((sum: number, day: any) => 
            sum + parseInt(day.daily_non_anonymous_threads), 0);
        } else {
          this.totalThreads = 0;
          this.totalAnonymous = 0;
          this.totalAuthenticated = 0;
        }

        this.initThreadsChart();
      });
  }

  private mergeThreadResponses(responses: any[]): any {
    const byDate = new Map<string, any>();
    const bucket = (responses && responses[0] && responses[0].bucket) || "day";

    for (const response of responses || []) {
      for (const row of response?.data || []) {
        const dateKey = row.date;
        if (!byDate.has(dateKey)) {
          byDate.set(dateKey, {
            date: dateKey,
            daily_total_threads: 0,
            daily_anonymous_threads: 0,
            daily_non_anonymous_threads: 0,
          });
        }

        const target = byDate.get(dateKey);
        target.daily_total_threads += Number(row.daily_total_threads || 0);
        target.daily_anonymous_threads += Number(row.daily_anonymous_threads || 0);
        target.daily_non_anonymous_threads += Number(row.daily_non_anonymous_threads || 0);
      }
    }

    const data = Array.from(byDate.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    return { data, bucket };
  }

  initThreadsChart() {
    // Check if we have the expected data structure
    if (!this.threadsData || !this.threadsData.data || !Array.isArray(this.threadsData.data)) {
      console.warn('Assistant Threads Widget - Missing expected data structure:', this.threadsData);
      return;
    }
    
    const type = this.bucket;

    const threadsDataObj: {
      type: "day" | "week" | "month" | "quarter" | "year";
      data: {
        date: Date;
        total: number;
        anonymous: number;
        nonAnonymous: number;
      }[];
    } = { type, data: [] };

    if (type === "day" || type === "week" || type === "month" || type === "quarter" || type === "year") {
      for (let i = 0; i < this.threadsData.data.length; i++) {
        const dayData = this.threadsData.data[i];
        
        threadsDataObj.data.push({
          date: new Date(dayData.date),
          total: parseInt(dayData.daily_total_threads),
          anonymous: parseInt(dayData.daily_anonymous_threads),
          nonAnonymous: parseInt(dayData.daily_non_anonymous_threads),
        });
      }
    }

    this.threadsChartOptions = {
      grid: {
        left: 30,
        top: 0,
        right: 30,
        bottom: 30,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: threadsDataObj.data.map((data: any) => data.date.toISOString()),
        axisLabel: {
          color: "#fff",
          hideOverlap: false,
          formatter: (d: any) => {
            return this.getXLabel(d, type as any);
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisPointer: {
          type: 'shadow'
        }
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: false,
        },
        show: false,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any, ticket: any, callback: any) => {
          return this.getTooltip(params, type as any, params[0].axisValue);
        },
      },


      series: [
        {
          name: 'Total',
          data: threadsDataObj.data.map((data: any) => data.total),
          showSymbol: false,
          label: {
            color: "#7dc4fd",
          },
          type: "bar",
          itemStyle: { color: "white", borderRadius: [5, 5, 0, 0] },
          large: true,
          barMaxWidth: "40%",
        },
        {
          name: 'Anonymous',
          data: threadsDataObj.data.map((data: any) => data.anonymous),
          showSymbol: false,
          type: "line",
          itemStyle: { color: "#ff6b6b" },
          lineStyle: { color: "#ff6b6b" },
        },
        {
          name: 'Non-Anonymous',
          data: threadsDataObj.data.map((data: any) => data.nonAnonymous),
          showSymbol: false,
          type: "line",
          itemStyle: { color: "#2d8a7d" },
          lineStyle: { color: "#2d8a7d" },
        }
      ],
    };
  }

  private getQuarter(date: Date): number {
    return Math.floor(date.getMonth() / 3) + 1;
  }

  private getXLabel(dateString: string, type: "day" | "week" | "month" | "quarter" | "year"): string {
    const date = new Date(dateString);
    if (type === "day") {
      return this.getDayLabel(date);
    } else if (type === "week") {
      return this.getWeekLabel(date);
    } else if (type === "month") {
      return this.getMonthLabel(date);
    } else if (type === "quarter") {
      return `Q${this.getQuarter(date)} ${date.getFullYear()}`;
    } else if (type === "year") {
      return `${date.getFullYear()}`;
    }
    return "";
  }

  private getDayLabel(date: Date) {
    // Check if we have more than 30 days of data
    if (this.threadsData && this.threadsData.data && this.threadsData.data.length > 30) {
      // Show every other day for better readability
      const day = date.getDate();
      return day % 2 === 0 ? "" : day.toString();
    }
    return date.getDate().toString();
  }

  getWeekLabel(date: Date) {
    const currentDate = date.getDate();
    if (currentDate < 7) {
      return MONTHS[date.getMonth()];
    }
    return `${date.getMonth()}/${date.getDate()}`;
  }

  getMonthLabel(date: Date) {
    const currentMonth = date.getMonth();
    if (currentMonth === 0) {
      return date.getFullYear().toString();
    }
    return MONTHS[currentMonth];
  }

  getTooltip(params: any, type: "day" | "week" | "month" | "quarter" | "year", dateString: string) {
    const date = new Date(dateString);

    if (type === "day") {
      return `
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}
      <br>
      <span style="color: black;"><strong>Total: ${params[0].value}</strong></span>
      <br>
      <span style="color: #ff6b6b;"><strong>Anonymous: ${params[1]?.value || 0}</strong></span>
      <br>
      <span style="color: #2d8a7d;"><strong>Non-Anonymous: ${params[2]?.value || 0}</strong></span>
      `;
    } else if (type === "week") {
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 7);
      return `
      Week Of:<br>
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()} - ${
        MONTHS[endDate.getMonth()]
      } ${endDate.getDate()} ${endDate.getFullYear()}
      <br>
      <span style="color: black;"><strong>Total: ${params[0].value}</strong></span>
      <br>
      <span style="color: #ff6b6b;"><strong>Anonymous: ${params[1]?.value || 0}</strong></span>
      <br>
      <span style="color: #2d8a7d;"><strong>Non-Anonymous: ${params[2]?.value || 0}</strong></span>
      `;
    } else if (type === "month") {
      return `
      ${MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}
      <br><span style="color: black;"><strong>Total: ${params[0].value}</strong></span>
      <br><span style="color: #ff6b6b;"><strong>Anonymous: ${params[1]?.value || 0}</strong></span>
      <br><span style="color: #2d8a7d;"><strong>Non-Anonymous: ${params[2]?.value || 0}</strong></span>
      `;
    } else if (type === "quarter") {
      return `
      Q${this.getQuarter(date)} ${date.getFullYear()}
      <br><span style="color: black;"><strong>Total: ${params[0].value}</strong></span>
      <br><span style="color: #ff6b6b;"><strong>Anonymous: ${params[1]?.value || 0}</strong></span>
      <br><span style="color: #2d8a7d;"><strong>Non-Anonymous: ${params[2]?.value || 0}</strong></span>
      `;
    } else if (type === "year") {
      return `
      ${date.getFullYear()}
      <br><span style="color: black;"><strong>Total: ${params[0].value}</strong></span>
      <br><span style="color: #ff6b6b;"><strong>Anonymous: ${params[1]?.value || 0}</strong></span>
      <br><span style="color: #2d8a7d;"><strong>Non-Anonymous: ${params[2]?.value || 0}</strong></span>
      `;
    }
    return "";
  }
} 